import { useState, useEffect, useRef, useCallback } from "react";
import { TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { cn } from "@/lib/utils";
import { SearchInput, filterSections } from "./SearchInput";
import { TableOfContents } from "./TableOfContents";
import {
  knowledgeBaseContent,
  categories,
  type KBSection,
} from "./knowledge-base-content";

export function KnowledgeBaseTab() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSection, setActiveSection] = useState("dashboard");
  const [filteredSections, setFilteredSections] = useState<string[]>(
    knowledgeBaseContent.map((s) => s.id)
  );
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());

  // Update filtered sections when search changes
  useEffect(() => {
    const filtered = filterSections(searchQuery);
    setFilteredSections(filtered);
  }, [searchQuery]);

  // Handle URL hash for deep linking
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash && knowledgeBaseContent.some((s) => s.id === hash)) {
      setActiveSection(hash);
      setTimeout(() => scrollToSection(hash), 100);
    }
  }, []);

  // Scroll spy - update active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const scrollTop = contentRef.current.scrollTop;
      const buffer = 100; // Pixels from top to consider "in view"

      let currentSection = activeSection;
      let minDistance = Infinity;

      sectionRefs.current.forEach((element, sectionId) => {
        const rect = element.getBoundingClientRect();
        const containerRect = contentRef.current!.getBoundingClientRect();
        const relativeTop = rect.top - containerRect.top;

        // Find the section closest to the top of the container
        if (relativeTop <= buffer && relativeTop > -rect.height / 2) {
          const distance = Math.abs(relativeTop);
          if (distance < minDistance) {
            minDistance = distance;
            currentSection = sectionId;
          }
        }
      });

      if (currentSection !== activeSection) {
        setActiveSection(currentSection);
        // Update URL hash without scrolling
        window.history.replaceState(null, "", `#${currentSection}`);
      }
    };

    const container = contentRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [activeSection]);

  const scrollToSection = useCallback((sectionId: string) => {
    const element = sectionRefs.current.get(sectionId);
    if (element && contentRef.current) {
      const containerRect = contentRef.current.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const scrollTop =
        contentRef.current.scrollTop + elementRect.top - containerRect.top - 20;

      contentRef.current.scrollTo({
        top: scrollTop,
        behavior: "smooth",
      });
    }
  }, []);

  const handleSectionClick = (sectionId: string) => {
    setActiveSection(sectionId);
    scrollToSection(sectionId);
    window.history.replaceState(null, "", `#${sectionId}`);
    setMobileNavOpen(false);
  };

  const handleSearchSelect = (sectionId: string) => {
    setActiveSection(sectionId);
    setSearchQuery("");
    setTimeout(() => scrollToSection(sectionId), 50);
    window.history.replaceState(null, "", `#${sectionId}`);
  };

  // Register section ref
  const registerSectionRef = useCallback(
    (sectionId: string, element: HTMLElement | null) => {
      if (element) {
        sectionRefs.current.set(sectionId, element);
      } else {
        sectionRefs.current.delete(sectionId);
      }
    },
    []
  );

  // Get sections organized by category for rendering
  const getSectionsToRender = () => {
    return categories
      .map((category) => {
        const sections = category.sections
          .map((sectionId) =>
            knowledgeBaseContent.find((s) => s.id === sectionId)
          )
          .filter(
            (s): s is KBSection =>
              s !== undefined && filteredSections.includes(s.id)
          );

        return sections.length > 0 ? { category, sections } : null;
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  };

  const sectionsToRender = getSectionsToRender();

  return (
    <TabsContent value="knowledge-base" className="space-y-4">
      {/* Mobile Navigation */}
      <div className="lg:hidden flex items-center gap-2">
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon">
              <Menu className="h-4 w-4" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72">
            <div className="mt-4">
              <h3 className="font-semibold mb-4">Table of Contents</h3>
              <TableOfContents
                activeSection={activeSection}
                onSectionClick={handleSectionClick}
                filteredSections={filteredSections}
              />
            </div>
          </SheetContent>
        </Sheet>
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          onSelect={handleSearchSelect}
          className="flex-1"
        />
      </div>

      {/* Desktop Layout */}
      <div className="flex gap-6">
        {/* Sidebar - Hidden on mobile */}
        <aside className="hidden lg:block w-64 flex-shrink-0">
          <div className="sticky top-0 space-y-4">
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              onSelect={handleSearchSelect}
            />
            <Card>
              <CardContent className="p-4">
                <TableOfContents
                  activeSection={activeSection}
                  onSectionClick={handleSectionClick}
                  filteredSections={filteredSections}
                />
              </CardContent>
            </Card>
          </div>
        </aside>

        {/* Main Content */}
        <main
          ref={contentRef}
          className="flex-1 min-w-0 max-h-[calc(100vh-200px)] overflow-y-auto pr-2"
        >
          <div className="space-y-8">
            {sectionsToRender.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <p className="text-muted-foreground">
                    No results found for "{searchQuery}"
                  </p>
                  <Button
                    variant="link"
                    onClick={() => setSearchQuery("")}
                    className="mt-2"
                  >
                    Clear search
                  </Button>
                </CardContent>
              </Card>
            ) : (
              sectionsToRender.map(({ category, sections }) => (
                <div key={category.id} className="space-y-4">
                  <h2 className="text-lg font-semibold text-muted-foreground border-b pb-2">
                    {category.title}
                  </h2>
                  <div className="space-y-4">
                    {sections.map((section) => {
                      const Icon = section.icon;
                      return (
                        <Card
                          key={section.id}
                          ref={(el) => registerSectionRef(section.id, el)}
                          id={section.id}
                          className={cn(
                            "transition-shadow",
                            activeSection === section.id &&
                              "ring-2 ring-primary/20"
                          )}
                        >
                          <CardHeader className="pb-2">
                            <div className="flex items-center gap-2">
                              <Icon className="h-5 w-5" />
                              <CardTitle className="text-lg">
                                {section.title}
                              </CardTitle>
                            </div>
                          </CardHeader>
                          <CardContent className="text-sm">
                            {section.content}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </main>
      </div>
    </TabsContent>
  );
}
